import { Injectable } from '@angular/core';

/**
 * Plays a "ding" for incoming notifications even when the OS/browser would
 * normally deliver them silently.
 *
 * Strategy:
 * - Uses the Web Audio API to synthesize a short two-tone chime (no asset
 *   file required, no network fetch).
 * - Creates the AudioContext lazily the first time we try to play, and
 *   also wires a one-shot user-gesture unlock. Browser autoplay policy
 *   requires at least one user interaction with the page before any audio
 *   can start — after that, subsequent plays work silently.
 * - Safe to call even before unlock: if the context is still "suspended"
 *   we request resume() and skip that play; the next play after a user
 *   gesture will succeed.
 *
 * Limitation:
 * - If the user has never interacted with the page, the first ding after
 *   a push may be inaudible. Once they click/type anywhere, all further
 *   pushes ring reliably.
 * - Fully-closed tabs cannot play audio; the OS notification handles
 *   that case (and its sound is controlled by the OS, not us).
 */
@Injectable({ providedIn: 'root' })
export class NotificationSoundService {
    private audioContext: AudioContext | null = null;
    private unlocked = false;
    private unlockHandlersAttached = false;

    /**
     * Called once from AppComponent.ngOnInit to arm a one-shot listener
     * that unlocks audio on the user's first interaction with the page.
     */
    armUnlockOnUserGesture(): void {
        if (this.unlockHandlersAttached || typeof document === 'undefined') return;
        const events: Array<keyof DocumentEventMap> = [
            'click',
            'keydown',
            'touchstart',
            'pointerdown',
        ];
        const unlock = () => {
            this.ensureContext();
            if (this.audioContext && this.audioContext.state === 'suspended') {
                void this.audioContext.resume().catch(() => undefined);
            }
            this.unlocked = true;
            for (const type of events) {
                document.removeEventListener(type, unlock, true);
            }
        };
        for (const type of events) {
            document.addEventListener(type, unlock, { capture: true, once: false });
        }
        this.unlockHandlersAttached = true;
    }

    /**
     * Plays a short two-tone chime. Returns immediately; the tones are
     * scheduled on the AudioContext clock.
     */
    play(): void {
        const ctx = this.ensureContext();
        if (!ctx) return;

        if (ctx.state === 'suspended') {
            // Try to resume, but don't fail loudly — the first play after
            // the very first user gesture still often works.
            void ctx.resume().catch(() => undefined);
        }

        const now = ctx.currentTime;
        // Two-tone "ding-dong": 880 Hz then 660 Hz, each ~180 ms.
        this.scheduleTone(ctx, 880, now, 0.18, 0.2);
        this.scheduleTone(ctx, 660, now + 0.22, 0.22, 0.2);
    }

    private ensureContext(): AudioContext | null {
        if (this.audioContext) return this.audioContext;
        const AC =
            (window as unknown as { AudioContext?: typeof AudioContext })
                .AudioContext ||
            (window as unknown as { webkitAudioContext?: typeof AudioContext })
                .webkitAudioContext;
        if (!AC) return null;
        try {
            this.audioContext = new AC();
        } catch {
            this.audioContext = null;
        }
        return this.audioContext;
    }

    /**
     * Schedules a single tone with a quick attack/release envelope so it
     * sounds like a chime rather than a click/buzz.
     */
    private scheduleTone(
        ctx: AudioContext,
        frequency: number,
        startAt: number,
        durationSec: number,
        peakGain: number,
    ): void {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(frequency, startAt);

        const attack = 0.01;
        const release = Math.max(durationSec - attack, 0.05);
        gain.gain.setValueAtTime(0, startAt);
        gain.gain.linearRampToValueAtTime(peakGain, startAt + attack);
        gain.gain.exponentialRampToValueAtTime(
            0.0001,
            startAt + attack + release,
        );

        osc.connect(gain).connect(ctx.destination);
        osc.start(startAt);
        osc.stop(startAt + attack + release + 0.02);
    }

    /** For diagnostics / UI hints. */
    get isUnlocked(): boolean {
        return this.unlocked;
    }
}
